/**
 * searchUtils.js
 * Utility functions for search functionality
 */

/**
 * Determines if a query string is likely a question
 * 
 * @param {string} query - The search query to analyze
 * @returns {boolean} - True if the query appears to be a question
 */
export function isQuestion(query) {
  if (!query) return false;
  
  const trimmed = query.trim().toLowerCase();
  
  // Check for question marks
  if (trimmed.includes('?')) return true;
  
  // Check for common question starters
  const questionStarters = [
    'who', 'what', 'when', 'where', 'why', 'how',
    'can', 'could', 'would', 'should', 'is', 'are',
    'was', 'were', 'will', 'do', 'does', 'did',
    'has', 'have', 'had', 'explain', 'tell me',
    'which', 'whose'
  ];
  
  for (const starter of questionStarters) {
    if (trimmed.startsWith(starter + ' ')) {
      return true;
    }
  }
  
  return false;
}

/**
 * Track a company visit in local storage for quicker access in the future
 * 
 * @param {Object} company - The company data to save
 */
export function trackRecentlyViewedCompany(company) {
  try {
    const recentCompanies = JSON.parse(localStorage.getItem('recentCompanies') || '[]');
    
    // Remove if already exists
    const filtered = recentCompanies.filter(c => c.id !== company.id);
    
    // Add to beginning (most recent)
    filtered.unshift(company);
    
    // Keep only last 10
    const updated = filtered.slice(0, 10);
    
    localStorage.setItem('recentCompanies', JSON.stringify(updated));
  } catch (error) {
    console.error('Failed to track recently viewed company:', error);
  }
}

/**
 * Get recently viewed companies from local storage
 * 
 * @returns {Array} - List of recently viewed companies
 */
export function getRecentlyViewedCompanies() {
  try {
    return JSON.parse(localStorage.getItem('recentCompanies') || '[]');
  } catch (error) {
    console.error('Failed to get recently viewed companies:', error);
    return [];
  }
}

/**
 * Validate recently viewed companies against user's access permissions.
 * Removes companies the user no longer has access to.
 * Called on auth/token refresh to keep localStorage in sync with access control.
 *
 * @param {Function} apiPost - API client function for making requests
 * @returns {Promise<void>}
 */
/**
 * Remove a single company from recently viewed (e.g., when access is denied)
 *
 * @param {string} companyId - ID of the company to remove
 */
export function removeFromRecentlyViewed(companyId) {
  try {
    const recentCompanies = getRecentlyViewedCompanies();
    const filtered = recentCompanies.filter(c => c.id !== companyId);
    if (filtered.length !== recentCompanies.length) {
      localStorage.setItem('recentCompanies', JSON.stringify(filtered));
      console.log('[searchUtils] Removed company from recently viewed:', companyId);
    }
  } catch (error) {
    console.error('[searchUtils] Failed to remove from recently viewed:', error);
  }
}

export async function validateRecentlyViewedCompanies(apiPost) {
  try {
    const recentCompanies = getRecentlyViewedCompanies();
    console.log('[searchUtils] Validating recently viewed companies:', recentCompanies.length);
    if (recentCompanies.length === 0) return;

    const companyIds = recentCompanies.map(c => c.id);
    console.log('[searchUtils] Sending company IDs for validation:', companyIds);
    const response = await apiPost('user/validate-company-access', { company_ids: companyIds });

    if (!response.ok) {
      console.error('[searchUtils] Failed to validate company access:', response.status);
      return;
    }

    const { accessible_ids } = await response.json();
    const accessibleSet = new Set(accessible_ids);

    // Filter out inaccessible companies
    const filtered = recentCompanies.filter(c => accessibleSet.has(c.id));

    if (filtered.length !== recentCompanies.length) {
      console.log('[searchUtils] Removed', recentCompanies.length - filtered.length, 'inaccessible companies from recently viewed');
      localStorage.setItem('recentCompanies', JSON.stringify(filtered));
    }
  } catch (error) {
    console.error('[searchUtils] Error validating recently viewed companies:', error);
  }
} 